import React, { FC, useCallback, useState } from 'react'
import './style.css'
import Modal from '@material-ui/core/Modal/index'
import { FormattedMessage } from 'react-intl'
import SchedulerTasksContainer from './components/SchedulerTasksContainer'
import UnscheduledTasksListContainer from './components/UnscheduledTasksListContainer'
import { ConnectedUnscheduledTasksProvider } from './context/UnscheduledTasksContext'
import { compose } from 'redux'
import { Grid, withStyles } from '@material-ui/core'
import { getStyles } from 'isotope-client'
import { colors } from '../../../utils/constants'
import ScheduleFiltersContainer from './components/filters/ScheduleFiltersContainer'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import PermasoftIconButton from '../../../components/IconButton'
import CloseIcon from '@material-ui/icons/Close'
import withTheme from '@material-ui/core/styles/withTheme'
import { Theme } from '@material-ui/core/styles/createMuiTheme'
import Slide from '@material-ui/core/Slide'
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import { SchedulerProvider } from './context/SchedulerContext'
import { injectTypeTacheValueList } from '../../common/valueLists/valueListInjectors'
import { ValueListEntry } from '../../../utils/types/global.types'
import { TaskPanelRedirectionParameters } from './services/schedule.types'
import TaskDetailContainer from './components/planning/TaskDetailContainer'

/**
 * Styles
 */
const styles = () => getStyles({
	modal: {
		display: 'flex',
		flex: 1
	},
	modalContainer: {
		display: 'flex',
		flex: 1,
		borderRadius: 20,
		flexDirection: 'column',
		margin: '12px',
		paddingLeft: 10,
		paddingRight: 10,
		paddingBottom: 10,
		backgroundColor: colors.etapeContrastBackground,
		overflow: 'hidden'
	},
	titleContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexDirection: 'row'
	},
	closeIcon: {
		position: 'absolute',
		right: 44
	},
	title: {
		flex: 1,
		display: 'flex',
		height: 50,
		alignItems: 'center',
		justifyContent: 'center',
		borderBottom: '2px solid #55555a',
		columnGap: 10
	},
	bodyContainer: {
		display: 'flex',
		flex: 1,
		flexDirection: 'row',
		overflow: 'hidden',
		gap: '12px'
	},
	leftBodyContainer: {
		position: 'relative',
		display: 'flex',
		flex: 1,
		flexDirection: 'column'
	},
	schedulerContainer: {
		flex: 1,
		overflow: 'auto',
		borderRadius: '10px'
	},
	unscheduledTaskListContainer: {
		display: 'flex',
		flexDirection: 'column' as 'column',
		width: 350
	},
	collapseContainer: {
		position: 'absolute',
		right: 0,
		top: 10,
		width: 30
	}
} as const)

const taskPanelStyle = {
	position: 'absolute' as 'absolute',
	display: 'flex' as 'flex',
	width: 450,
	right: 22,
	height: 'calc(100% - 76px)',
	borderRadius: '0px 0px 12px 20px',
	zIndex: 10
}

const backdropStyle = {
	position: 'absolute' as 'absolute',
	display: 'flex' as 'flex',
	width: 'calc(100vw - 474px)',
	left: 12,
	height: 'calc(100% - 76px)',
	backgroundColor: 'rgba(0, 0, 0, 0.4)',
	borderRadius: '0px 0px 12px 20px',
	zIndex: 10
}

type SchedulerPageProps = {
	ressourcesToDisplay: string,
	open: boolean
	typeTacheList: ValueListEntry[]
	isResultat: boolean
	handleClose: () => void
	classes: ClassNameMap
	theme: Theme
}

const SchedulerPage: FC<SchedulerPageProps> = ({ ressourcesToDisplay, open, typeTacheList, isResultat, handleClose, classes, theme }) => {
	const [showUnscheduledTask, setShowUnscheduledTask] = useState<boolean>(true)
	const [taskPanelRedirectionParameters, setTaskPanelRedirectionParameters] = useState<TaskPanelRedirectionParameters | undefined>(undefined)

	const closeModal = useCallback(() => {
		handleClose()
		setTaskPanelRedirectionParameters(undefined)
	}, [handleClose, setTaskPanelRedirectionParameters])

	return (
		<Modal
			open={open}
			onClose={closeModal}
			aria-labelledby="SchedulerPage"
			aria-describedby="SchedulerPage"
			className={classes.modal}
		>
			<div className={classes.modalContainer}>
				<div className={classes.titleContainer}>
					<div className={classes.title}>
						<Typography component="h3" variant="h3">
							<FormattedMessage id={`schedule.title.${isResultat ? 'result' : 'planning'}`} />
						</Typography>
					</div>
					<div className={classes.closeIcon}>
						<IconButton onClick={closeModal}>
							<CloseIcon />
						</IconButton>
					</div>
				</div>
				<SchedulerProvider isResultat={isResultat} typeTacheList={typeTacheList} ressourcesToDisplay={ressourcesToDisplay}>
					<div className={classes.bodyContainer}>
						<div
							className={classes.leftBodyContainer}
							style={{
								width: showUnscheduledTask ? 'calc(100% - 360px)' : '100%'
							}}
						>
							<ScheduleFiltersContainer />
							<Grid container className={classes.collapseContainer}>
								<PermasoftIconButton
									onClick={() => {
										setShowUnscheduledTask(!showUnscheduledTask)
									}}
									size="small"
									tooltip={undefined}
									tooltipProps={undefined}
									disableRipple
									disableFocusRipple
								>
									{showUnscheduledTask ? <KeyboardArrowRightIcon /> : <KeyboardArrowLeftIcon />}
								</PermasoftIconButton>
							</Grid>
							<div className={classes.schedulerContainer} style={{ border: `2px solid ${theme.palette.primary.main}` }}>
								<SchedulerTasksContainer setTaskPanelRedirectionParameters={setTaskPanelRedirectionParameters} elementsTypesToDisplay={ressourcesToDisplay}/>
							</div>
						</div>
						<Slide direction="left" in={showUnscheduledTask} mountOnEnter unmountOnExit>
							<div className={classes.unscheduledTaskListContainer}>
								<ConnectedUnscheduledTasksProvider isResultat={isResultat}>
									<UnscheduledTasksListContainer
										setTaskPanelRedirectionParameters={setTaskPanelRedirectionParameters}
										isResultat={isResultat}
									/>
								</ConnectedUnscheduledTasksProvider>
							</div>
						</Slide>

						{!!taskPanelRedirectionParameters?.id && <div style={backdropStyle} />}
						<Slide
							direction="left"
							in={!!taskPanelRedirectionParameters?.id}
							style={taskPanelStyle}
							timeout={0}
							mountOnEnter
							unmountOnExit
						>
							<div>
								{taskPanelRedirectionParameters?.id && <TaskDetailContainer
									isResultat={isResultat}
									taskPanelRedirection={taskPanelRedirectionParameters}
									setTaskPanelRedirectionParameters={setTaskPanelRedirectionParameters}
								/>}
							</div>
						</Slide>
					</div>
				</SchedulerProvider>
			</div>
		</Modal>
	)
}

export default compose(
	withTheme,
	withStyles(styles),
	injectTypeTacheValueList
)(SchedulerPage)